import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, isValidElement, useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { useLatest } from 'react-use';
import styled from 'styled-components';
import { useHapticFeedback } from '../..';
import { useBorderBoxSizeCallback } from '../../lib/react';
import { Button } from '../button';
import { ArrowLeft2Icon, ArrowRight2Icon } from '../icons';
import { SegmentedControlContext } from './segment-control-context';
import { SegmentControlItem } from './segment-control-item';
const OVERFLOW_TOLERANCE_PX = 1;
// TODO: merge with SegmentControl?
export const SegmentControlScrollable = ({ value, onChange, getId, disabled, size = 'h24', className, children, viewportRef, }) => {
    const viewportInternalRef = useRef(null);
    const trackRef = useRef(null);
    // Keep the internal ref (source of truth for measurements) and the optional
    // forwarded ref pointing at the same viewport element.
    const setViewportRef = useCallback((node) => {
        viewportInternalRef.current = node;
        if (viewportRef) {
            viewportRef.current = node;
        }
    }, [viewportRef]);
    const items = Children.toArray(children).filter(isValidElement);
    const [offset, setOffset] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [mask, setMask] = useState({ left: false, right: false });
    const onChangeRef = useLatest(onChange);
    const haptics = useHapticFeedback();
    const getItemId = useCallback((itemValue) => (getId ? getId(itemValue) : String(itemValue)), [getId]);
    const ctx = useMemo(() => ({
        value: value,
        getId: getId ? (value) => getId(value) : undefined,
        onChange: (value) => {
            if (onChangeRef.current) {
                haptics.impactOccurred('light');
                onChangeRef.current?.(value);
            }
        },
    }), [value, getId, haptics, onChangeRef]);
    const selectedIndex = items.findIndex((item) => getItemId(item.props.value) === getItemId(value));
    const emitChange = useCallback((nextValue) => {
        if (onChangeRef.current) {
            haptics.impactOccurred('light');
            onChangeRef.current(nextValue);
        }
    }, [haptics, onChangeRef]);
    const selectByIndex = useCallback((index) => {
        const item = items[index];
        if (!item || item.props.disabled) {
            return;
        }
        emitChange(item.props.value);
    }, [emitChange, items]);
    const showPrev = useCallback(() => {
        if (selectedIndex > 0) {
            selectByIndex(selectedIndex - 1);
        }
    }, [selectByIndex, selectedIndex]);
    const showNext = useCallback(() => {
        if (selectedIndex >= 0 && selectedIndex < items.length - 1) {
            selectByIndex(selectedIndex + 1);
        }
    }, [items.length, selectByIndex, selectedIndex]);
    const update = () => {
        const viewport = viewportInternalRef.current;
        const track = trackRef.current;
        if (!viewport || !track) {
            return;
        }
        const viewportWidth = viewport.clientWidth;
        const trackWidth = track.scrollWidth;
        const overflow = trackWidth > viewportWidth + OVERFLOW_TOLERANCE_PX;
        setHasOverflow(overflow);
        const selectedEl = track.children[selectedIndex];
        if (!selectedEl) {
            setOffset(0);
            setMask({ left: false, right: false });
            return;
        }
        const itemCenter = selectedEl.offsetLeft + selectedEl.offsetWidth / 2;
        const minOffset = Math.min(0, viewportWidth - trackWidth);
        const nextOffset = Math.max(minOffset, Math.min(0, viewportWidth / 2 - itemCenter));
        setOffset(nextOffset);
        setMask({
            left: overflow && nextOffset < -OVERFLOW_TOLERANCE_PX,
            right: overflow && nextOffset > minOffset + OVERFLOW_TOLERANCE_PX,
        });
    };
    useLayoutEffect(() => {
        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex, value, items.length, hasOverflow]);
    useBorderBoxSizeCallback(viewportInternalRef, update);
    const isFirst = selectedIndex <= 0;
    const isLast = selectedIndex < 0 || selectedIndex >= items.length - 1;
    return (_jsx(SegmentedControlContext.Provider, { value: ctx, children: _jsxs(Root, { "data-size": size, className: className, "data-overflow": hasOverflow ? true : undefined, "aria-disabled": disabled, children: [hasOverflow ? (_jsx(NavButton, { type: "button", variant: "secondary", radius: "full", size: size, disabled: isFirst, onClick: showPrev, iconLeft: _jsx(ArrowLeft2Icon, { size: 10 }) })) : undefined, _jsx(Viewport, { ref: setViewportRef, "data-size": size, "data-mask-left": mask.left ? true : undefined, "data-mask-right": mask.right ? true : undefined, children: _jsx(Track, { ref: trackRef, style: { transform: `translateX(${offset}px)` }, children: items.map((item) => (_jsx(Item, { ...item.props }, item.key))) }) }), hasOverflow ? (_jsx(NavButton, { type: "button", variant: "secondary", radius: "full", size: size, disabled: isLast, onClick: showNext, iconLeft: _jsx(ArrowRight2Icon, { size: 10 }) })) : undefined] }) }));
};
const Item = styled(SegmentControlItem) `
  border-radius: 15px;
  background: var(--backgroundA-2);
`;
SegmentControlScrollable.Item = Item;
const Root = styled.div `
  // TODO: #111 is too bright, add new variable  --background-0?
  /* background: var(--background-1); */
  background: var(--appearance-1);

  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 2px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px;

  &:not([data-overflow='true']) {
    grid-template-columns: minmax(0, 1fr);
  }

  border-radius: 20px;
`;
const Viewport = styled.div `
  --segment-control-fade: 16px;

  overflow: hidden;
  width: 100%;
  min-width: 0;

  &[data-size='h24'] {
    --segment-control-height: 24px;
    --segment-control-item-padding-inline: 8px;
    --segment-control-item-radius: 15px;
  }
  &[data-size='h28'] {
    --segment-control-height: 28px;
    --segment-control-item-padding-inline: 8px;
    --segment-control-item-radius: 15px;
  }

  height: var(--segment-control-height);

  &[data-mask-left='true'][data-mask-right='true'] {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      var(--background-1) var(--segment-control-fade),
      var(--background-1) calc(100% - var(--segment-control-fade)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      var(--background-1) var(--segment-control-fade),
      var(--background-1) calc(100% - var(--segment-control-fade)),
      transparent
    );
  }

  &[data-mask-left='true']:not([data-mask-right='true']) {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      var(--background-1) var(--segment-control-fade)
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      var(--background-1) var(--segment-control-fade)
    );
  }

  &:not([data-mask-left='true'])[data-mask-right='true'] {
    -webkit-mask-image: linear-gradient(
      to right,
      var(--background-1) calc(100% - var(--segment-control-fade)),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      var(--background-1) calc(100% - var(--segment-control-fade)),
      transparent
    );
  }
`;
const Track = styled.div `
  display: inline-flex;
  align-items: stretch;
  gap: 1px;
  height: 100%;
  width: max-content;

  transition: transform 0.2s ease;
  will-change: transform;
`;
const NavButton = styled(Button) `
  color: var(--text-9);
`;
//# sourceMappingURL=segment-control-scrollable.js.map